import React, { Component } from 'react';
import { Link } from 'react-router-dom';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Bir sonraki render'da hata UI'ını göstermek için state'i güncelle
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Hata detaylarını component state'ine kaydet
    this.setState({ 
      error: error, 
      errorInfo: errorInfo 
    });
    
    // Hata loglaması yapılabilir
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <>
          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

            .eb-wrapper {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #f1f5f9;
              font-family: 'Inter', system-ui, sans-serif;
              padding: 24px;
            }

            .eb-card {
              background: #ffffff;
              border-radius: 20px;
              box-shadow: 0 4px 6px -1px rgba(0,0,0,0.07), 0 20px 40px -10px rgba(0,0,0,0.1);
              padding: 48px 40px;
              max-width: 520px;
              width: 100%;
              text-align: center;
            }

            .eb-icon-wrap {
              width: 80px;
              height: 80px;
              background: linear-gradient(135deg, #fef3c7, #fed7aa);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 28px;
            }

            .eb-icon-wrap svg {
              width: 36px;
              height: 36px;
              color: #d97706;
            }

            .eb-title {
              font-size: 1.5rem;
              font-weight: 700;
              color: #1e293b;
              margin: 0 0 10px;
              letter-spacing: -0.02em;
            }

            .eb-desc {
              font-size: 0.9375rem;
              color: #64748b;
              line-height: 1.6;
              margin: 0 0 32px;
            }

            .eb-actions {
              display: flex;
              gap: 12px;
              justify-content: center;
              flex-wrap: wrap;
            }

            .eb-btn {
              display: inline-flex;
              align-items: center;
              gap: 8px;
              padding: 10px 22px;
              border-radius: 10px;
              font-size: 0.875rem;
              font-weight: 600;
              cursor: pointer;
              text-decoration: none;
              transition: all 0.18s ease;
              border: none;
            }

            .eb-btn-secondary {
              background: #f1f5f9;
              color: #475569;
              border: 1.5px solid #e2e8f0;
            }

            .eb-btn-secondary:hover {
              background: #e2e8f0;
              color: #1e293b;
              transform: translateY(-1px);
            }

            .eb-btn-primary {
              background: #1e293b;
              color: #ffffff;
            }

            .eb-btn-primary:hover {
              background: #0f172a;
              color: #ffffff;
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(15,23,42,0.3);
            }

            .eb-details-block {
              background: #0f172a;
              border-radius: 12px;
              padding: 16px 20px;
              text-align: left;
              margin-bottom: 28px;
              border: 1px solid #1e293b;
            }

            .eb-details-summary {
              color: #94a3b8;
              font-size: 0.8rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              cursor: pointer;
              list-style: none;
              display: flex;
              align-items: center;
              gap: 6px;
              user-select: none;
            }

            .eb-details-summary::-webkit-details-marker { display: none; }

            .eb-details-content {
              margin-top: 12px;
              font-size: 0.78rem;
              color: #ef4444;
              font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
              word-break: break-all;
              white-space: pre-wrap;
              max-height: 160px;
              overflow-y: auto;
            }

            .eb-details-stack {
              color: #64748b;
              margin-top: 8px;
              font-size: 0.72rem;
            }
          `}</style>

          <div className="eb-wrapper">
            <div className="eb-card">

              {/* Icon */}
              <div className="eb-icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>

              <h2 className="eb-title">Beklenmedik bir hata oluştu</h2>
              <p className="eb-desc">
                Sayfayı yenilemeyi deneyin. Sorun devam ederse lütfen destek ekibiyle iletişime geçin.
              </p>

              {/* Error details — only in development mode */}
              {this.props.showDetails && this.state.error && (
                <div className="eb-details-block">
                  <details>
                    <summary className="eb-details-summary">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-2h2zm0-4h-2V7h2z"/></svg>
                      Hata Detayları
                    </summary>
                    <div className="eb-details-content">
                      {this.state.error.toString()}
                      {this.state.errorInfo && (
                        <div className="eb-details-stack">
                          {this.state.errorInfo.componentStack}
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              )}

              {/* Actions */}
              <div className="eb-actions">
                <button
                  className="eb-btn eb-btn-secondary"
                  onClick={() => window.location.reload()}
                >
                  <i className="fas fa-rotate-right" />
                  Sayfayı Yenile
                </button>
                <Link to="/" className="eb-btn eb-btn-primary">
                  <i className="fas fa-house" />
                  Ana Sayfaya Dön
                </Link>
              </div>

            </div>
          </div>
        </>
      );
    }

    // Hata yoksa children'ı render et
    return this.props.children;
  }
}

export default ErrorBoundary;